import * as React from 'react';
import { AnalyticsContext } from '@atlaskit/analytics-next';

import { Logo } from '@atlassian/help-center-common-component/logo';
import i18n from './messages';
import { FormattedMessage } from 'react-intl';
import { AvatarMenu } from '../avatar-menu';
import { SearchButton } from '../search-button';
import type { UserRequestsModel } from 'state/persisted/user/reducer';
import type { Organisations } from '../requests-menu';
import { RequestsMenu } from '../requests-menu';
import { Root, Nav, NavItem, NavList, LoginButton, PortalLink, ExternalNavLink } from './styled';
import type { UserRequestsCounts } from 'state/persisted/user-requests-counts';
import { Skeleton } from '@atlaskit/avatar';
import { getHelpCenterUrl } from '@atlassian/help-center-common-util/url';
import { Search } from 'view/search';
import { isSearchEnhancementsMilestone1Enabled } from 'feature-flags';

export interface Props {
    isAnonymous?: boolean;
    isUserLoaded?: boolean;
    userLoggedIn?: boolean;
    searchShown?: boolean;
    loginUrl?: string;
    logoTextColor: 'banner' | 'theme';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    openSearch: () => any;
    hideSearchButton?: boolean;
    isCustomizing?: boolean;
    userRequest: UserRequestsModel | UserRequestsCounts; //TODO: remove UserRequestsModel when removing isSplitRequestCountsFromUserModelEnabled
    organisations: Organisations[];
    anonymousEnabled?: boolean;
    isAdvancedHelpCenter?: boolean;
}

const Header: React.StatelessComponent<Props> = ({
    isAnonymous,
    isUserLoaded,
    userLoggedIn,
    searchShown,
    openSearch,
    loginUrl,
    logoTextColor,
    hideSearchButton,
    userRequest,
    organisations,
    anonymousEnabled,
    isAdvancedHelpCenter,
}) => {
    const defaultLoginUrl = `/user/login?destination=${getHelpCenterUrl().replace(/\//g, '')}`;

    const authenticatedNavLinks = (
        <>
            <NavItem>
                <AnalyticsContext data={{ actionSubjectId: 'navbarRequests' }}>
                    <RequestsMenu
                        approvalCount={userRequest.approvalCount || 0}
                        pendingApprovalCount={userRequest.pendingApprovalCount || 0}
                        myRequestCount={userRequest.myRequestCount || 0}
                        organisations={organisations}
                    />
                </AnalyticsContext>
            </NavItem>

            <NavItem>
                <AnalyticsContext data={{ actionSubjectId: 'navbarAvatar' }}>
                    <AvatarMenu />
                </AnalyticsContext>
            </NavItem>
        </>
    );

    const unauthenticatedNavLinks = (
        <NavItem>
            <ExternalNavLink>
                <LoginButton href={loginUrl ? loginUrl : defaultLoginUrl} actionSubjectId="navbarLogin">
                    <FormattedMessage {...i18n.login} />
                </LoginButton>
            </ExternalNavLink>
        </NavItem>
    );

    const anonymousAccessNotEnbledAndUserNotLoggedIn = !(!!anonymousEnabled || !!userLoggedIn);

    let portalLinkUrl;
    if (isAnonymous || anonymousAccessNotEnbledAndUserNotLoggedIn) {
        portalLinkUrl = defaultLoginUrl;
    } else portalLinkUrl = getHelpCenterUrl();

    return (
        <AnalyticsContext data={{ source: 'navbar' }}>
            <Root isAdvancedHelpCenter={Boolean(isAdvancedHelpCenter)}>
                <PortalLink to={portalLinkUrl} logoTextColor={logoTextColor} actionSubjectId="navbarHelpCenter">
                    <React.Suspense fallback={<Skeleton appearance="square" size="medium" />}>
                        <Logo isAdvancedHelpCenter={Boolean(isAdvancedHelpCenter)} />
                    </React.Suspense>
                </PortalLink>
                <Nav>
                    <NavList>
                        {isUserLoaded && (
                            <>
                                {hideSearchButton || (
                                    <NavItem>
                                        {isSearchEnhancementsMilestone1Enabled() ? (
                                            <Search trigger="icon" />
                                        ) : (
                                            <SearchButton shown={!!searchShown} onClick={openSearch} />
                                        )}
                                    </NavItem>
                                )}

                                {userLoggedIn ? authenticatedNavLinks : unauthenticatedNavLinks}
                            </>
                        )}
                    </NavList>
                </Nav>
            </Root>
        </AnalyticsContext>
    );
};

export default Header;
